import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, InputAdornment, MenuItem, Stack, Typography } from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { useGetBatchTypesByCodesQuery } from 'services/dinaBatchApi';
import {
  KB_BATCH_TYPE_CODE,
  getBatchIcon,
  BATCH_PARAM_TYPE_CODE
} from 'pages/knowledgeBase/batch/batchUtils';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';

/**
 * Component for displaying and managing the details of a batch. Allows viewing,
 * creating, and updating batches.
 *
 * @component
 *
 * @example
 * <DinaBatchDetails batch={batch} onSubmit={handleSubmit} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.batch - The batch object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 *
 * @returns {React.Component} The Batch Details component.
 */
function DinaBatchDetails({ batch, onSubmit, onFieldChange }) {
  const { formatMessage: msg } = useIntl();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      batchType: null,
      cron: '',
      parameters: []
    }
  });

  const { fields, replace } = useFieldArray({
    control,
    name: 'parameters'
  });

  const { data: batchTypes, isLoading: batchTypesLoading } = useGetBatchTypesByCodesQuery(
    Object.values(KB_BATCH_TYPE_CODE)
  );

  const watchedBatchType = watch('batchType');

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  // Initialize parameters when batch type changes
  useEffect(() => {
    if (watchedBatchType?.paramTypes) {
      const existingParams = watch('parameters') || [];

      const newParameters = watchedBatchType.paramTypes
        .filter((paramType) => paramType.code !== BATCH_PARAM_TYPE_CODE.KNOWLEDGE_BASE_CODE)
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((paramType) => {
          const existingParam = existingParams.find(
            (p) =>
              p.batchParamType?.id === paramType.id || p.batchParamType?.code === paramType.code
          );
          const paramValue = existingParam?.paramValue || paramType.defaultValue || '';
          return {
            batchParamType: paramType,
            paramValue: paramValue
          };
        });
      replace(newParameters);
    }
  }, [watchedBatchType, replace, watch]);

  const resetBatch = useCallback(() => {
    if (batch?.id) {
      const batchData = {
        id: batch.id,
        batchType: batch.batchType,
        cron: batch.cron || '',
        parameters: batch.parameters || []
      };
      reset(batchData);
    } else {
      const defaultBatchType =
        batchTypes?.find((type) => type.code === 'FILE_PROCESSING_JOB') ||
        (batchTypes && batchTypes.length > 0 ? batchTypes[0] : null);
      reset({
        id: '',
        batchType: defaultBatchType,
        cron: '',
        parameters: []
      });
    }
  }, [batch, reset, batchTypes]);

  useEffect(() => {
    resetBatch();
  }, [batch, resetBatch]);

  if (batchTypesLoading) {
    return <IndeterminateLoading />;
  }

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const clearParameterField = (index) => {
    setValue(`parameters.${index}.paramValue`, '');
  };

  const handleReset = () => {
    resetBatch();
  };

  const renderParameterField = (paramType, field, index) => {
    const fieldProps = {
      ...field,
      label: paramType.label,
      variant: 'filled',
      fullWidth: true,
      size: 'small',
      error: !!errors.parameters?.[index]?.paramValue,
      helperText: errors.parameters?.[index]?.paramValue?.message || paramType.description,
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={msg({ id: 'tooltip.clear' })}>
              <IconButton onClick={() => clearParameterField(index)}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        )
      }
    };

    // Render different input types based on baseType
    switch (paramType.baseType?.toLowerCase()) {
      case 'number':
        return <MDInput {...fieldProps} type="number" required={paramType.isRequired} />;
      case 'boolean':
        return (
          <MDInput {...fieldProps} select required={paramType.isRequired}>
            <MenuItem value="true">{msg({ id: 'common.yes' })}</MenuItem>
            <MenuItem value="false">{msg({ id: 'common.no' })}</MenuItem>
          </MDInput>
        );
      case 'textarea':
        return <MDInput {...fieldProps} multiline rows={3} required={paramType.isRequired} />;
      default:
        return <MDInput {...fieldProps} required={paramType.isRequired} />;
    }
  };

  const isCreating = !batch?.id;

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <Icon color="text" fontSize="medium" sx={{ mr: '0.75rem' }}>
          {getBatchIcon(watchedBatchType?.code)}
        </Icon>
        <MDTypography variant="h5">
          {msg({
            id: isCreating
              ? 'page.batch.details.creation.title'
              : 'page.batch.details.modification.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <Controller
            name="id"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.id.label' })}
                variant="filled"
                fullWidth
                required
                size="small"
                InputProps={{
                  readOnly: true
                }}
              />
            )}
          />
        )}

        <Controller
          name="batchType"
          control={control}
          rules={{ required: msg({ id: 'field.batchType.required' }) }}
          render={({ field }) => (
            <MDInput
              {...field}
              select
              label={msg({ id: 'field.batchType.label' })}
              variant="filled"
              fullWidth
              required
              size="small"
              error={!!errors.batchType}
              helperText={errors.batchType ? errors.batchType.message : ''}
              value={field.value?.id || ''}
              onChange={(e) => {
                const selectedType = batchTypes?.find((type) => type.id === e.target.value);
                field.onChange(selectedType);
              }}
            >
              {batchTypes?.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.label}
                </MenuItem>
              ))}
            </MDInput>
          )}
        />

        <Controller
          name="cron"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.cron.label' })}
              variant="filled"
              fullWidth
              size="small"
              placeholder="0 0 12 * * ?"
              helperText={msg({ id: 'field.cron.helper' })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('cron')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />

        {/* Parameters Section */}
        {fields.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" color="text" sx={{ mb: 2 }}>
              {msg({ id: 'field.parameters.label' })}
            </Typography>
            <Stack spacing={2}>
              {fields.map((field, index) => (
                <Controller
                  key={field.id}
                  name={`parameters.${index}.paramValue`}
                  control={control}
                  rules={{
                    required: field.batchParamType?.isRequired
                      ? msg(
                          { id: 'field.parameter.required' },
                          {
                            name: field.batchParamType.label
                          }
                        )
                      : false
                  }}
                  render={({ field: paramField }) =>
                    renderParameterField(field.batchParamType, paramField, index)
                  }
                />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary" disabled={batchTypesLoading}>
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

DinaBatchDetails.propTypes = {
  batch: PropTypes.shape({
    id: PropTypes.number,
    batchType: PropTypes.object,
    cron: PropTypes.string,
    parameters: PropTypes.array
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired
};

export default DinaBatchDetails;
